// ENG-YAKSON-080 (A7): the shared, synchronously-readable home for the current
// user's RESOLVED permissions (GET /auth/my-permissions). This is the linchpin
// that moves nav/route/menu gating off the client-side userType matrix onto the
// backend's role-resolved permission map.
//
// Ergonomics mirror the old access-rule cache: fetch async on session-ready,
// cache the map in a context, and expose a SYNC `hasPermission(key)` so callers
// (navTree prune, ProtectedRoute, UserMenu) stay synchronous. Until the fetch
// settles — or if it fails — `ready` is false and callers fall back to the
// legacy matrix (see `useGating`), so the nav never flashes empty and legacy
// behavior holds through the transition window (Spec A §12).
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  myPermissionsApiAuthMyPermissionsGet,
  type MyPermissionsPermissions,
  type RoleSummary,
} from '@/lib/api/generated';
import { useAuth } from './auth-context';
import type { PermissionKey } from './permission-keys';

type Status = 'idle' | 'loading' | 'ready' | 'error';

// A10 / manual-QA hook: dispatch this on window to force a re-fetch of the
// resolved permission map WITHOUT a re-login — proves cache-flush coherence
// after an admin edits a role/override. Mirrors the existing
// 'yakson-auth:logout' window-event convention.
export const PERMISSIONS_REFETCH_EVENT = 'yakson-permissions:refetch';

interface MyPermissionsContextValue {
  /** Raw resolved map: permissionKey → 'FULL' | 'OWN' | 'NONE'. Empty until ready. */
  permissions: MyPermissionsPermissions;
  role: RoleSummary | null;
  /** HQ = a staff member whose outlet (or Department) is HQ — not the ADMIN
   *  login (owner, 15 Oct 2026). False until the fetch resolves. */
  isHq: boolean;
  /** Leave manager (Angela, 22 Oct 2026): admin, a named leave manager, or —
   *  while none is named — HQ staff. Mirrors the server's `manages_leave`. */
  managesLeave: boolean;
  status: Status;
  /** True once the fetch has resolved successfully (a usable map is present). */
  ready: boolean;
  /** Still fetching (callers should legacy-fall-back meanwhile). */
  loading: boolean;
  /** The first fetch for the signed-in user has finished (ready OR error).
   *  Stays true through later re-fetches, so a refetch never blanks a page.
   *  The route guard waits for this before deciding (direct-link fix). */
  settled: boolean;
  /** Sync read: does the user resolve FULL or OWN on ANY of the given key(s)? */
  hasPermission: (key: PermissionKey | PermissionKey[]) => boolean;
  /** Re-fetch the map (cache-flush-without-relog). Safe to call anytime. */
  refetch: () => Promise<void>;
}

const MyPermissionsContext = createContext<MyPermissionsContextValue | undefined>(undefined);

const EMPTY_PERMISSIONS: MyPermissionsPermissions = {};

export function MyPermissionsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<MyPermissionsPermissions>(EMPTY_PERMISSIONS);
  const [role, setRole] = useState<RoleSummary | null>(null);
  const [isHq, setIsHq] = useState(false);
  const [managesLeave, setManagesLeave] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [settled, setSettled] = useState(false);

  const fetchPermissions = useCallback(async () => {
    setStatus('loading');
    try {
      const me = await myPermissionsApiAuthMyPermissionsGet();
      setPermissions(me.permissions ?? EMPTY_PERMISSIONS);
      setRole(me.role ?? null);
      setIsHq(me.isHq ?? false);
      setManagesLeave(me.managesLeave ?? false);
      setStatus('ready');
      setSettled(true);
    } catch {
      // Leave any previously-loaded map in place is unsafe (could be a different
      // user); reset to empty and mark error so callers legacy-fall-back.
      setPermissions(EMPTY_PERMISSIONS);
      setRole(null);
      setIsHq(false);
      setManagesLeave(false);
      setStatus('error');
      setSettled(true);
    }
  }, []);

  // Fetch on session-ready; reset when the user logs out. Re-runs when the
  // authenticated principal changes (id), so switching accounts re-resolves.
  useEffect(() => {
    if (!user) {
      setPermissions(EMPTY_PERMISSIONS);
      setRole(null);
      setIsHq(false);
      setManagesLeave(false);
      setStatus('idle');
      setSettled(false);
      return;
    }
    void fetchPermissions();
  }, [user, fetchPermissions]);

  // A10 / manual-QA: window-event-driven refetch (cache-flush-without-relog).
  useEffect(() => {
    const onRefetch = () => {
      if (user) void fetchPermissions();
    };
    window.addEventListener(PERMISSIONS_REFETCH_EVENT, onRefetch);
    return () => window.removeEventListener(PERMISSIONS_REFETCH_EVENT, onRefetch);
  }, [user, fetchPermissions]);

  const hasPermission = useCallback(
    (key: PermissionKey | PermissionKey[]): boolean => {
      const keys = Array.isArray(key) ? key : [key];
      return keys.some((k) => {
        const level = permissions[k];
        return level === 'FULL' || level === 'OWN';
      });
    },
    [permissions],
  );

  const value = useMemo<MyPermissionsContextValue>(
    () => ({
      permissions,
      role,
      isHq,
      managesLeave,
      status,
      ready: status === 'ready',
      loading: status === 'loading' || status === 'idle',
      settled,
      hasPermission,
      refetch: fetchPermissions,
    }),
    [permissions, role, isHq, managesLeave, status, settled, hasPermission, fetchPermissions],
  );

  return <MyPermissionsContext.Provider value={value}>{children}</MyPermissionsContext.Provider>;
}

/** HQ or not, without requiring the provider (false outside it / until loaded). */
export function useIsHq(): boolean {
  return useContext(MyPermissionsContext)?.isHq ?? false;
}

export function useMyPermissions(): MyPermissionsContextValue {
  const ctx = useContext(MyPermissionsContext);
  if (!ctx) {
    throw new Error('useMyPermissions must be used within a MyPermissionsProvider');
  }
  return ctx;
}
